import React, { createContext, useContext, useState, useCallback, useRef } from "react";
import { SortingState } from "@tanstack/react-table";
import { useQuery, useMutation } from "@tanstack/react-query";
import { usePost } from "../API";
import { logAdminPanelError } from "../utils/adminPanel.utils";
import { AuditLog, FilterParams, FilterOptions } from "../AdminPanel/AuditLogs/types";

export type ExportFormat = 'csv' | 'xlsx';

// Visible columns config for export - matches the pageConfig structure
export type VisibleColumnsConfig = {
  timestamp?: boolean;
  eventType?: boolean;
  userName?: boolean;
  userId?: boolean;
  userEmail?: boolean;
};

export type ExportJobStatus = {
  taskId: string;
  status: 'QUEUED' | 'STARTED' | 'FINISHED' | 'FAILED';
  result?: {
    downloadUrl?: string;
    filename?: string;
    error?: string;
    data?: {
      downloadUrl?: string;
      filename?: string;
    };
  };
};


type AuditLogsContextType = {
  auditLogs: Array<AuditLog>;
  getAuditLogs: (params?: { page?: number; limit?: number; sorting?: SortingState; search?: string; filters?: FilterParams }) => void;
  isAuditLogsLoading: boolean;
  isAuditLogsSearching: boolean;
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  auditLogsSearch: string;
  auditLogsSearchDisplay: string;
  auditLogsSorting: SortingState;
  auditLogsFilters: FilterParams;
  filterOptions: FilterOptions | null;
  onAuditLogsPaginationChange: (pagination: { pageIndex: number; pageSize: number }) => void;
  onAuditLogsSearchChange: (search: string) => void;
  onAuditLogsSortingChange: React.Dispatch<React.SetStateAction<SortingState>>;
  onAuditLogsFiltersChange: (filters: FilterParams) => void;
  // Export functionality
  initiateExport: (params: { format: ExportFormat; visibleColumns?: VisibleColumnsConfig }) => Promise<{ taskId: string }>;
  exportJob: ExportJobStatus | null;
  isExporting: boolean;
};

type AuditLogsProviderProps = {
  children: React.ReactNode;
};

const AuditLogsContext = createContext<AuditLogsContextType | undefined>(undefined);

export const useAuditLogs = (): AuditLogsContextType => {
  const context = useContext(AuditLogsContext);
  if (!context) {
    throw new Error("useAuditLogs must be used within an AuditLogsProvider");
  }
  return context;
};

export const AuditLogsProvider: React.FC<AuditLogsProviderProps> = ({ children }) => {
  // UI state for search debouncing
  const [auditLogsSearchDisplay, setAuditLogsSearchDisplay] = useState('');
  const [auditLogsSearch, setAuditLogsSearch] = useState('');
  const [isAuditLogsSearching, setIsAuditLogsSearching] = useState(false);
  const searchTimeoutRef = useRef<NodeJS.Timeout>();

  // Query parameters state
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [auditLogsSorting, setAuditLogsSorting] = useState<SortingState>([]);
  const [auditLogsFilters, setAuditLogsFilters] = useState<FilterParams>({});

  // Export state
  const [exportJob, setExportJob] = useState<ExportJobStatus | null>(null);

  const getAuditLogsData = usePost("/api/admin_panel/user_audit_log");
  const exportApi = usePost("/api/admin_panel/audit_logs/export");
  const exportStatusApi = usePost("/api/admin_panel/audit_logs/export/status");

  // Convert SortingState to backend format
  const backendSorting = auditLogsSorting.map(sort => ({
    sortBy: sort.id as 'event' | 'timestamp' | 'userName',
    sortOrder: sort.desc ? 'desc' as const : 'asc' as const
  }));

  // Main query for fetching audit logs
  const {
    data: auditLogsData,
    isLoading: isAuditLogsLoading,
    refetch,
  } = useQuery({
    queryKey: ['auditLogs', page, limit, backendSorting, auditLogsSearch, auditLogsFilters],
    queryFn: async () => {
      const requestParams = {
        page,
        limit,
        ...(backendSorting.length > 0 && { sorting: backendSorting }),
        ...(auditLogsSearch && { search: auditLogsSearch }),
        ...(Object.keys(auditLogsFilters).length > 0 && { filters: auditLogsFilters })
      };

      const result = await getAuditLogsData(requestParams).catch((err) => {
        console.error("getAuditLogsData threw error:", err);
        return undefined;
      });

      if (result && result.success && result.data) {
        return {
          auditLogs: result.data.auditLogs?.data || [],
          total: result.data.auditLogs?.total || 0,
          page: result.data.auditLogs?.page || 1,
          limit: result.data.auditLogs?.limit || 10,
          totalPages: result.data.auditLogs?.totalPages || 0,
          filterOptions: result.data.auditLogs?.filterOptions || null,
        };
      } else if (result && result.error) {
        logAdminPanelError(result.error, result.message);
      } else {
        console.error("Audit logs API returned undefined or invalid response:", result);
      }

      return {
        auditLogs: [],
        total: 0,
        page: 1,
        limit: 10,
        totalPages: 0,
        filterOptions: null,
      };
    },
    staleTime: 30 * 1000, // Consider data fresh for 30 seconds
    gcTime: 5 * 60 * 1000, // Keep in cache for 5 minutes
    placeholderData: (previousData) => previousData, // Show old data while fetching
  });

  // Export status polling query
  const { data: exportStatusData } = useQuery({
    queryKey: ['auditLogsExportStatus', exportJob?.taskId],
    queryFn: async () => {
      if (!exportJob?.taskId) return null;
      const response = await exportStatusApi({ taskId: exportJob.taskId });
      if (response.success && response.data?.exportJob) {
        return response.data.exportJob as ExportJobStatus;
      }
      return null;
    },
    enabled: !!exportJob?.taskId && (exportJob.status === 'QUEUED' || exportJob.status === 'STARTED'),
    refetchInterval: 3000, // Poll every 3 seconds
  });

  // Update export job when status changes
  React.useEffect(() => {
    if (exportStatusData) {
      setExportJob(exportStatusData);
    }
  }, [exportStatusData]);

  const isExporting = exportJob?.status === 'QUEUED' || exportJob?.status === 'STARTED';

  // Export mutation
  const exportMutation = useMutation({
    mutationFn: async (params: { format: ExportFormat; visibleColumns?: VisibleColumnsConfig }) => {
      const response = await exportApi({
        format: params.format,
        filters: auditLogsFilters,
        visibleColumns: params.visibleColumns
      });

      if (response.success && response.data) {
        return { taskId: response.data.taskId };
      }

      throw new Error(response.message || 'Failed to initiate export');
    },
    onSuccess: (data) => {
      setExportJob({
        taskId: data.taskId,
        status: 'QUEUED'
      });
    },
    onError: (error) => {
      console.error('Error initiating audit log export:', error);
    },
  });

  const initiateExport = useCallback(async (params: { format: ExportFormat; visibleColumns?: VisibleColumnsConfig }) => {
    return exportMutation.mutateAsync(params);
  }, [exportMutation]);

  // Wrapper function for backwards compatibility
  const getAuditLogs = useCallback((params?: { page?: number; limit?: number; sorting?: SortingState; search?: string; filters?: FilterParams }) => {
    if (params?.page !== undefined) setPage(params.page);
    if (params?.limit !== undefined) setLimit(params.limit);
    if (params?.sorting !== undefined) setAuditLogsSorting(params.sorting);
    if (params?.search !== undefined) setAuditLogsSearch(params.search);
    if (params?.filters !== undefined) setAuditLogsFilters(params.filters);
    // The query will automatically refetch when state changes
  }, []);

  const onAuditLogsPaginationChange = useCallback(
    (pagination: { pageIndex: number; pageSize: number }) => {
      // Convert 0-based pageIndex to 1-based page for API
      setPage(pagination.pageIndex + 1);
      setLimit(pagination.pageSize);
    },
    []
  );

  const onAuditLogsSortingChange = useCallback(
    (updaterOrValue: SortingState | ((old: SortingState) => SortingState)) => {
      setAuditLogsSorting(prevSorting => {
        const newSorting = typeof updaterOrValue === 'function' ? updaterOrValue(prevSorting) : updaterOrValue;
        return newSorting;
      });
      setPage(1); // Reset to first page on sort change
    },
    []
  );

  const onAuditLogsSearchChange = useCallback((search: string) => {
    // Update display value immediately for UI responsiveness
    setAuditLogsSearchDisplay(search);

    // Clear existing timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Set searching state if we have a search value
    if (search.trim() !== auditLogsSearch.trim()) {
      setIsAuditLogsSearching(true);
    }

    // Debounce the actual search
    searchTimeoutRef.current = setTimeout(() => {
      setAuditLogsSearch(search);
      setPage(1); // Reset to first page on search
      setIsAuditLogsSearching(false);
    }, 500);
  }, [auditLogsSearch]);

  const onAuditLogsFiltersChange = useCallback((filters: FilterParams) => {
    setAuditLogsFilters(filters);
    setPage(1); // Reset to first page on filter change
  }, []);

  // Extract data from query result
  const auditLogs = auditLogsData?.auditLogs || [];
  const total = auditLogsData?.total || 0;
  const totalPages = auditLogsData?.totalPages || 0;
  const filterOptions = auditLogsData?.filterOptions || null;

  const contextValue: AuditLogsContextType = {
    auditLogs,
    getAuditLogs,
    isAuditLogsLoading,
    isAuditLogsSearching,
    total,
    page,
    limit,
    totalPages,
    auditLogsSearch,
    auditLogsSearchDisplay,
    auditLogsSorting,
    auditLogsFilters,
    filterOptions,
    onAuditLogsPaginationChange,
    onAuditLogsSearchChange,
    onAuditLogsSortingChange,
    onAuditLogsFiltersChange,
    // Export functionality
    initiateExport,
    exportJob,
    isExporting,
  };

  return <AuditLogsContext.Provider value={contextValue}>{children}</AuditLogsContext.Provider>;
};
